import { compactNumber, cx } from "shared/utils";
import { TabStepsType } from "views/applications-id-report";
import {
  Verification,
  VerificationNames,
  VerificationStatus,
  PartnerFeatures
} from "shared/types";
import { StatusIcon } from "shared/app/VerificationStatusIcon";
import {
  mapCombinedVerificationNameToTitle,
  mapCombinedVerificationToStatus
} from "utils/combineVerifications";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Select, Show } from "shared/components";
import { useAuthContext } from "providers/Auth";
import { Application } from "types/application";
import { useChatContext } from "stream-chat-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useVerificationsOverviewController,
  VERIFICATIONS_WITH_WARNINGS
} from "shared/queries/useVerificationsOverviewController";
import { Loader } from "shared/components/Loader";
import { VerificationReport } from "types/landlord/reports";
import api from "api/landlord/applications";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import {
  AttachmentIcon,
  BubbleChatIcon,
  Certificate01Icon,
  FaceIdIcon,
  FileVerifiedIcon,
  SquareLock01Icon,
  User02Icon,
  Wallet04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Home05Icon } from "shared/icons/hugeicons-corrected/icons/home-05-stroke";
import { CreditCard01Icon } from "shared/icons/hugeicons-corrected/untitled/credit-card-01";
import { DogIcon } from "shared/icons/hugeicons-corrected/lucide/dog";
import { Logout03Icon } from "shared/icons/hugeicons-corrected/icons/logout-03-stroke";
import { SecurityIcon } from "shared/icons/hugeicons-corrected/icons/security";

const SidebarIcon = ({ icon }: { icon: IconSvgElement }) => (
  <HugeiconsIcon icon={icon} className="text-inherit shrink-0" size={20} />
);

const mapTabNameToIcon = (name: TabStepsType | string) => {
  switch (name) {
    case VerificationNames.RentalApplication:
      return <SidebarIcon icon={User02Icon} />;
    case VerificationNames.IdentityVerification:
      return <SidebarIcon icon={FaceIdIcon} />;
    case VerificationNames.CombinedIncome:
    case VerificationNames.IncomeVerification:
    case VerificationNames.VerifiedIncomeVerification:
      return <SidebarIcon icon={Wallet04Icon} />;
    case VerificationNames.HousingHistory:
      return <SidebarIcon icon={Home05Icon} />;
    case VerificationNames.CreditReport:
      return <SidebarIcon icon={CreditCard01Icon} />;
    case VerificationNames.EvictionReport:
      return <SidebarIcon icon={Logout03Icon} />;
    case VerificationNames.CriminalReport:
    case VerificationNames.CanadianCriminalReport:
      return <SidebarIcon icon={SecurityIcon} />;
    case VerificationNames.SubmissionDisclosure:
      return <SidebarIcon icon={FileVerifiedIcon} />;
    case VerificationNames.TermsAndConditions:
      return <SidebarIcon icon={Certificate01Icon} />;
    case VerificationNames.PetScreeningReport:
      return <SidebarIcon icon={DogIcon} />;
    default:
      return;
  }
};

interface SidebarProps {
  applicantId: number;
  application: Application;
  requiresOverridesIdentityVerification: boolean;
  tabSteps: (Verification | Verification[])[];
  currentTab: TabStepsType;
  setCurrentTab: (tabName: TabStepsType) => void;
  isInvitee?: boolean;
}

const PAGE_PADDING = 24,
  HEADER_HEIGHT = 180;

export const Sidebar = ({
  applicantId,
  application,
  requiresOverridesIdentityVerification,
  tabSteps: verifications,
  currentTab,
  setCurrentTab,
  isInvitee
}: SidebarProps) => {
  const { data } = useQuery({
    queryFn: () =>
      instance
        .get(`/portal/v1/applicants/${applicantId}/additional_documents`, {
          timeout: 300_000 // 5 minutes
        })
        .then(({ data }) => data),
    queryKey: ["ADDITIONAL_DOCUMENTS", applicantId]
  });

  const { data: allCreditReports } = useQuery<VerificationReport[]>({
    queryKey: ["CREDIT_REPORTS", application.id, applicantId],
    queryFn: () =>
      api
        .getApplicant(application.id, applicantId)
        .then((res) => res.applicant.credit_report_verifications)
  });

  const { verificationsOverviewMeta } = useVerificationsOverviewController({
    applicationId: application.id,
    applicantId: applicantId
  });

  const { type, partnerFeatures, permissions } = useAuthContext();

  const hasPermission = useCallback(
    (name: TabStepsType) => {
      switch (name) {
        case VerificationNames.RentalApplication:
          return permissions.canAccessReports.rentalApplication;
        case VerificationNames.IdentityVerification:
          return permissions.canAccessReports.identityVerification;
        case VerificationNames.IncomeVerification:
        case VerificationNames.CombinedIncome:
        case VerificationNames.VerifiedIncomeVerification:
          return permissions.canAccessReports.incomeReport;
        case VerificationNames.HousingHistory:
          return permissions.canAccessReports.housingHistoryReport;
        case VerificationNames.CreditReport:
          return permissions.canAccessReports.creditReport;
        case VerificationNames.CriminalReport:
          return permissions.canAccessReports.usCriminalReport;
        case VerificationNames.EvictionReport:
          return permissions.canAccessReports.housingCourtReport;
        case VerificationNames.CanadianCriminalReport:
          return permissions.canAccessReports.caCriminalReport;
        case VerificationNames.PetScreeningReport:
          return permissions.canAccessReports.animalScreening;
        case VerificationNames.SubmissionDisclosure:
          return permissions.canAccessReports.disclosure;
        case "additional_documents":
          return permissions.canAccessReports.additionalDocumentsReport;
        default:
          return true;
      }
    },
    [permissions]
  );

  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat) && !isInvitee;
  const currentApplicant = application.applicants.find(
    (app) => Number(app.id) === Number(applicantId)
  );

  const isIdentityVerificationOverrideRequired =
    requiresOverridesIdentityVerification;

  const { client } = useChatContext();

  const [unreads, setUnreads] = useState(0);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- client can be undefined at runtime outside <Chat>
    if (isInvitee || !client) return;

    let cancelled = false;
    const channel = client.channel("messaging", `chat-id-${applicantId}`);

    // Fetch state without subscribing — ApplicantChatSlice owns watch/stopWatching
    channel
      .query()
      .then(() => {
        if (!cancelled) setUnreads(channel.countUnread());
      })
      .catch(() => {});

    // Client-level listener catches events regardless of watch state
    const subscription = client.on((event) => {
      if (!event.cid || event.cid !== channel.cid) return;

      if (event.type === "notification.message_new") {
        channel
          .query()
          .then(() => {
            if (!cancelled) setUnreads(channel.countUnread());
          })
          .catch(() => {});
        return;
      }

      setUnreads(channel.countUnread());
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [client, applicantId, isInvitee]);

  const navigationOptions = useMemo(() => {
    const ret = hasChat
      ? [
          {
            value: "chat",
            label: "Chat"
          }
        ]
      : [];

    ret.push(
      ...verifications
        .filter((tabStep: Verification | Verification[]) => {
          if (Array.isArray(tabStep)) return tabStep.length === 2;

          return true;
        })
        .map((tabStep: Verification | Verification[]) => {
          const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;
          return {
            value: String(verification.name),
            label: String(mapCombinedVerificationNameToTitle(tabStep))
          };
        })
    );

    ret.push({
      value: "additional_documents",
      label: "Additional documents"
    });

    return ret;
  }, [hasChat]);

  const hasPendingCountyReports = Boolean(
    currentApplicant?.pending_county_reports
  );

  return (
    <>
      <div
        className={cx(
          "no-print -top-5 mb-8 p-2 lg:sticky lg:mb-0 lg:p-4 md:block hidden",
          // h-px is used to keep sticky sidebar sticky
          "lg:h-[750px]"
        )}
      >
        {/* Chat with applicant */}
        <Show
          if={
            type.isBoomScreen &&
            hasChat &&
            Boolean(!currentApplicant?.is_manual)
          }
        >
          <div
            className={cx(
              "flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3 transition-all duration-150",
              currentTab === "chat"
                ? "rounded-lg bg-primary-20 stroke-primary-500"
                : "stroke-content-1000 hover:bg-alpha-40"
            )}
            onClick={() => {
              document.getElementById("report_section")?.scrollTo({
                top: HEADER_HEIGHT + PAGE_PADDING,
                behavior: "smooth"
              });
              setCurrentTab("chat");
            }}
          >
            <div
              className={cx(
                "flex flex-shrink w-full items-center gap-2 stroke-inherit relative",
                currentTab === "chat" ? "text-primary-500" : "text-content-1000"
              )}
            >
              <SidebarIcon icon={BubbleChatIcon} />
              <p className="truncate text-sm font-medium">Chat</p>
              {unreads ? (
                <div className="bg-primary-500 text-white rounded text-xxs px-1 ml-auto min-w-[16px] text-center">
                  {compactNumber(unreads)}
                </div>
              ) : null}
            </div>
          </div>

          <div className="h-px bg-alpha-80 my-2" />
        </Show>
        {verifications
          .filter((tabStep: Verification | Verification[]) => {
            if (Array.isArray(tabStep)) return tabStep.length === 2;

            return true;
          })
          .map((tabStep: Verification | Verification[]) => {
            const verification = Array.isArray(tabStep) ? tabStep[0] : tabStep;

            const isSelected = currentTab === verification.name;

            const pendingStatuses = [
              VerificationStatus.Pending,
              VerificationStatus.Started,
              VerificationStatus.Skipped
            ];
            const notStartedOrInProgressOrSkipped =
              verification.name === VerificationNames.CreditReport
                ? allCreditReports?.every((cr) =>
                    pendingStatuses.includes(cr.status)
                  )
                : pendingStatuses.includes(verification.status);

            const activeCreditReport = allCreditReports?.find(
              (cr) => !cr.archived_at
            );
            const verificationStatus =
              verification.name === VerificationNames.CreditReport &&
              activeCreditReport
                ? activeCreditReport.status
                : mapCombinedVerificationToStatus(verification);

            const notStartedClassnames = notStartedOrInProgressOrSkipped
              ? "text-content-100"
              : "text-content-1000";

            return (
              <div
                className={cx(
                  "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg px-3 py-2 transition-all duration-150",
                  isSelected &&
                    "rounded-lg bg-primary-20 stroke-primary-500 text-primary-500",
                  notStartedOrInProgressOrSkipped
                    ? "stroke-content-100 text-content-100"
                    : "stroke-content-1000 hover:bg-alpha-40 text-content-1000"
                )}
                onClick={() => {
                  document.getElementById("report_section")?.scrollTo({
                    top: HEADER_HEIGHT + PAGE_PADDING,
                    behavior: "smooth"
                  });
                  setCurrentTab(verification.name as TabStepsType);
                }}
                key={verification.name}
              >
                <div className="flex flex-shrink items-center gap-2 stroke-inherit text-inherit">
                  {mapTabNameToIcon(verification.name)}
                  <p
                    className={cx(
                      "truncate text-sm font-medium",
                      isSelected ? "text-primary-600" : notStartedClassnames
                    )}
                  >
                    {mapCombinedVerificationNameToTitle(tabStep)}
                  </p>
                </div>

                <div className="flex items-center justify-center">
                  {VERIFICATIONS_WITH_WARNINGS.includes(verification.name) &&
                  verificationsOverviewMeta.isLoading ? (
                    <Loader className="size-4 animate-appear" />
                  ) : (
                    <>
                      <Show if={hasPermission(verification.name)}>
                        <StatusIcon
                          hasWarnings={
                            verificationsOverviewMeta.data?.[verification.name]
                              ?.has_warnings || false
                          }
                          isSsnMismatched={
                            verificationsOverviewMeta.data?.[
                              VerificationNames.CreditReport
                            ]?.ssn_mismatched || false
                          }
                          isAwaitingForCounty={
                            verification.name ===
                              VerificationNames.CriminalReport &&
                            hasPendingCountyReports
                          }
                          applicantStatus={
                            application.applicants.find(
                              (app) => app.id === applicantId
                            )?.application_status
                          }
                          isIdentityVerificationOverrideRequired={Boolean(
                            isIdentityVerificationOverrideRequired
                          )}
                          verificationStatus={verificationStatus}
                          isManuallyOverridden={
                            verification.status ===
                            VerificationStatus.FinishedByOverridden
                          }
                          name={verification.name}
                          executionMode={verification.settings?.execution_mode}
                          applicationId={application.id}
                          applicantId={applicantId}
                          verificationId={verification.id}
                          isRerun={verification.is_rerun}
                          isSelected={isSelected}
                        />
                      </Show>
                      <Show if={!hasPermission(verification.name)}>
                        <HugeiconsIcon
                          icon={SquareLock01Icon}
                          size={16}
                          className={cx(
                            "shrink-0 stroke-inherit",
                            isSelected && "text-primary-500",
                            notStartedClassnames
                          )}
                          strokeWidth={2}
                        />
                      </Show>
                    </>
                  )}
                </div>
              </div>
            );
          })}

        <div className="border-t border-alpha-40 my-2" />

        <div
          className={cx(
            "my-1 flex h-[44px] cursor-pointer justify-between gap-2 rounded-lg p-3 transition-all duration-150",
            currentTab === "additional_documents"
              ? "rounded-lg bg-primary-20 stroke-primary-500"
              : "stroke-content-1000 hover:bg-alpha-40"
          )}
          onClick={() => {
            document.getElementById("report_section")?.scrollTo({
              top: HEADER_HEIGHT + PAGE_PADDING,
              behavior: "smooth"
            });
            setCurrentTab("additional_documents");
          }}
        >
          <div
            className={cx(
              "flex flex-shrink items-center gap-2 stroke-inherit",
              currentTab === "additional_documents"
                ? "text-primary-500"
                : "text-content-1000"
            )}
          >
            <SidebarIcon icon={AttachmentIcon} />
            <p className={cx("truncate text-sm font-medium")}>
              Additional documents
            </p>
          </div>
          <div className="flex items-center justify-center">
            <p
              className={cx(
                "text-sm font-medium",
                currentTab === "additional_documents"
                  ? "text-primary-600"
                  : "text-content-1000"
              )}
            >
              {hasPermission("additional_documents") ? (
                data?.total_count || 0
              ) : (
                <HugeiconsIcon
                  icon={SquareLock01Icon}
                  size={16}
                  className={cx(
                    "shrink-0 stroke-inherit",
                    currentTab === "additional_documents" && "text-primary-500"
                  )}
                  strokeWidth={2}
                />
              )}
            </p>
          </div>
        </div>
      </div>

      <Select
        id="report-selector"
        className="md:hidden"
        onChange={(e) => {
          setCurrentTab(e.target.value as TabStepsType);
        }}
        value={currentTab}
        options={navigationOptions}
      />
    </>
  );
};
